import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../wrapper/API';
import { isAdmin } from '../../util/auth';
import { ROUTES } from '../../util/routes';
import { eventRules } from '../../util/eventRules';
import { mailSlotLabel } from '../../util/mailSlots';
import { formatEuro, owesPayment } from '../../util/payment';
import { addOnLineItems, formatAddOnQty, linesTotal, onSiteLines, signupLines } from '../../util/addOns';
import { groupRoleLabel, resolveGroupAndRole } from '../../util/participantGroups';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import useRoleSettings from '../../hooks/useRoleSettings';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useContextMenu } from '../context/ContextMenuContext';
import { usePopup } from '../context/PopupContext';
import ListItem from '../list/ListItem';
import ListTextItem from '../list/ListTextItem';
import ListSymbols from '../list/ListSymbols';
import ListSymbol from '../list/ListSymbol';
import ListColorExtension from '../list/ListColorExtension';
import ContextMenu from '../context-menu/ContextMenu';
import ContextMenuItem from '../context-menu/ContextMenuItem';
import ContextMenuSeparator from '../context-menu/ContextMenuSeparator';
import Popup from '../popup/Popup';
import Icon from '../home/Icon';
import AddOnChips from '../add-on/AddOnChips';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import { describeExhaustedAddOns, withReductions } from './addOnMessages';
import '../styles/MainContainer.css';
import '../pages/Registrations.css';
import './EventPlayerCard.css';
import './EventList.css';
import './UnregisteredPlayersSection.css';

// Zeigt abgemeldete (soft-gelöschte) Teilnehmer eines Spieltags als eigenes Card-Element unter
// der aktiven Liste (gleicher <main>-Stil). Zeilen verwenden dasselbe ListItem-Primitiv und
// dasselbe 6-Spalten-Grid wie die aktive Liste, ausgegraut via type="grayedout".
// Kontextmenü bietet E-Mail (analog aktive Liste), Wiederherstellen (POST /EventPlayer/{id}/Restore)
// und Dauerhaft löschen (DELETE /EventPlayer/{id}/Permanent) mit Bestätigungs-Popup.
// Gesamtbetrag analog zu EventList — Preis (Eintritt + Anmelde-Zusätze) + im Event gebuchte Artikel.
function getPlayerTotal(post) {
  return (post.price ?? 0) + linesTotal(onSiteLines(post));
}

// Zelle 4 = Chips der Anmelde-Zusätze (immer gerendert, positionsbasiertes Grid, siehe EventPlayerCard.css).
// Wiederherstellen behält die Anmelde-Zusätze; ist ein Zusatz mit „Ausgebucht → Warteliste“ voll, fragt das Popup
// nach der Zusatz-Warteliste, Zusätze ohne Warteliste werden gekürzt (Erfolgsmeldung).
// event: EventDetailResponse (or null while loading) — prepayment rule via util/eventRules.
function UnregisteredPlayersSection({ eventId, event = null, onRestore, refreshKey = 0 }) {
  const { byId: groupsById } = useParticipantGroups();
  // Configured role names of the group labels (groupRoleLabel) — re-render once they are loaded.
  useRoleSettings();
  const prepaymentRequired = eventRules(event).prepaymentRequired;
  const [unregistered, setUnregistered] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [pendingHardDelete, setPendingHardDelete] = useState(null);
  const [infoPost, setInfoPost] = useState(null);
  // Restore-Flow: wenn der Spieltag voll ist, kommt vom Backend "WouldBeWaitlisted" zurück;
  // der Admin entscheidet im Popup, ob er ihn auf die Warteliste setzt oder die Kapazität
  // überschreibt (analog zum Add-Flow in Event.js).
  const [pendingRestore, setPendingRestore] = useState(null);
  const [restoreWaitlistReason, setRestoreWaitlistReason] = useState(null);
  // Ausgebuchte Zusätze (AddOnCapacityInfo) bei Grund 'addOn'.
  const [restoreWaitlistAddOns, setRestoreWaitlistAddOns] = useState([]);
  const { contextMenuData } = useContextMenu();
  const { openPopup, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const navigate = useNavigate();
  // Defense-in-depth: Sektion ist komplett Admin-only — Backend blockt zusätzlich mit 403.
  const adminAllowed = isAdmin();

  useEffect(() => {
    if (!eventId || !adminAllowed) return;
    api.get(`/EventPlayer/Unregistered?eventId=${eventId}`)
      .then(res => setUnregistered(res.data ?? []))
      .catch(() => {});
  }, [eventId, adminAllowed, refreshKey]);

  if (!adminAllowed) return null;
  if (unregistered.length === 0) return null;

  // Group (+ role) at the event date as text — "Orga · Mitglied". In the rows (row = true) guests without
  // a role get the dot only, like the active list; the info popup always names the group.
  const groupLabelOf = (post, row = false) => {
    const { group, role } = resolveGroupAndRole(post, groupsById);
    if (row && group?.isGuest && !role) return '';
    return groupRoleLabel(group, role);
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE') + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const finalizeRestore = (id, target, participant) => {
    setUnregistered(prev => prev.filter(p => p.id !== id));
    setPendingRestore(null);
    setRestoreWaitlistReason(null);
    setRestoreWaitlistAddOns([]);
    if (onRestore) onRestore(participant ?? target);
  };

  const callRestore = (target, body) => {
    const loadingMessage = body?.participantType === 'Waitlist' || body?.forceAddOnWaitlist
      ? 'Spieler wird auf Warteliste gesetzt'
      : 'Spieler wird hinzugefügt';
    startSuccessBarLoading(loadingMessage);
    api.post(`/EventPlayer/${target.id}/Restore`, body ?? {})
      .then(res => {
        const result = res.data ?? {};
        if (result.status === 'WouldBeWaitlisted') {
          // Kapazität voll — Admin entscheidet via Popup.
          closeSuccessBar();
          setPendingRestore(target);
          setRestoreWaitlistReason(result.waitlistReason);
          setRestoreWaitlistAddOns(result.waitlistAddOns ?? []);
          openPopup('confirmrestoretowaitlist', target.id);
        } else if (result.status === 'Restored' && result.participant) {
          const restored = result.participant;
          const isWaitlist = restored.participantType === 'Waitlist' || restored.participantType === 3;
          // Ob eine Statusmail rausgeht, bestimmt die Mail-Regel der Gruppe (expectsAutoMail) — ohne
          // Adresse nie. Vorkasse + Zahlung offen: statt der Bestätigung geht die Zahlungsaufforderung raus
          // (Bestätigung kommt erst mit „Als bezahlt markieren“).
          const mailSent = !!restored.email && !!restored.expectsAutoMail;
          const paymentRequest = !isWaitlist && prepaymentRequired && owesPayment(restored) && !restored.isPaid;
          const base = isWaitlist ? 'Spieler auf Warteliste gesetzt' : 'Spieler hinzugefügt';
          // Zusätze ohne Warteliste, die nicht mehr frei waren, wurden gekürzt — nur gemeldet.
          startSuccessBarSuccess(withReductions(!mailSent
            ? base
            : paymentRequest
              ? `${base} — Mail „${mailSlotLabel('payment_request')}“ wird versendet`
              : `${base} — Mail wird versendet`, result.reducedAddOns));
          finalizeRestore(target.id, target, restored);
        } else {
          closeSuccessBar();
        }
      })
      .catch(() => closeSuccessBar());
  };

  const handleRestore = () => {
    const id = contextMenuData?.identifier;
    const target = unregistered.find(p => p.id === id);
    if (!target) return;
    callRestore(target, {});
  };

  // Spieltag voll → normale Warteliste; Zusatz ausgebucht → Zusatz-Warteliste (eigene Mail).
  const handleConfirmRestoreToWaitlist = () => {
    if (!pendingRestore) return;
    callRestore(pendingRestore, restoreWaitlistReason === 'addOn' ? { forceAddOnWaitlist: true } : { participantType: 'Waitlist' });
  };

  const handleSkipWaitlistOnRestore = () => {
    if (!pendingRestore) return;
    closePopup();
    callRestore(pendingRestore, { forceConfirm: true });
  };

  const handleEmail = () => {
    const target = unregistered.find(p => p.id === contextMenuData?.identifier);
    if (target?.playerId) navigate(ROUTES.mail(eventId, target.playerId));
  };

  const handleInfo = () => {
    const target = unregistered.find(p => p.id === contextMenuData?.identifier);
    if (!target) return;
    setInfoPost(target);
    openPopup('unregisteredplayerinfo', target.id);
  };

  const handleHardDeleteRequest = () => {
    const target = unregistered.find(p => p.id === contextMenuData?.identifier);
    if (!target) return;
    setPendingHardDelete(target);
    openPopup('confirmharddeleteeventplayer', target.id);
  };

  const handleHardDeleteConfirm = () => {
    if (!pendingHardDelete) return;
    const id = pendingHardDelete.id;
    startSuccessBarLoading('Spieler wird dauerhaft gelöscht');
    api.delete(`/EventPlayer/${id}/Permanent`)
      .then(() => {
        startSuccessBarSuccess('Spieler dauerhaft gelöscht');
        setUnregistered(prev => prev.filter(p => p.id !== id));
        setPendingHardDelete(null);
      })
      .catch(() => closeSuccessBar());
  };

  return (
    <main className="unregisteredPlayersMain">
      <button
        type="button"
        className={`unregisteredPlayersToggle ${expanded ? 'expanded' : ''}`}
        onClick={() => setExpanded(e => !e)}
      >
        <Icon className="unregisteredPlayersChevron" icon={expanded ? 'expand_more' : 'chevron_right'} />
        <span className="unregisteredPlayersTitle">Abgemeldete Spieler</span>
        <span className="unregisteredPlayersCount">{unregistered.length}</span>
      </button>
      {expanded && (
        <div className="unregisteredPlayersList">
          {unregistered.map((post, index) => (
            <ListItem
              key={post.id}
              identifier={post.id}
              gridTemplateColumns={'52px 1fr 1fr 52px 1fr 52px'}
              memo={post.memo}
              contextMenuIdentifier={isAdmin() ? 'unregisteredplayer' : ''}
              index={index}
              type={'grayedout'}
              className={'eventCard'}
            >
              <ParticipantGroupDot row={post} size='md' />
              <ListTextItem><s>{post.lastName ?? 'N/A'}</s></ListTextItem>
              <ListTextItem><s>{post.firstName ?? 'N/A'}</s></ListTextItem>
              <AddOnChips items={addOnLineItems(signupLines(post))} compact ariaLabel={'Gebuchte Zusätze'} />
              <ListTextItem>
                {getPlayerTotal(post) !== 0 && <s className="eventCardPrice">{formatEuro(getPlayerTotal(post))}</s>}
                {groupLabelOf(post, true) && <span className="eventCardGroupLabel">{groupLabelOf(post, true)}</span>}
              </ListTextItem>
              <div className="listTextContainer"></div>
              <ListSymbols>
                <ListSymbol icon={'delete'} visible={true} type={1} />
                <ListSymbol icon={'visibility_off'} visible={!!post.isAnonymized} type={1} />
                <ListSymbol icon={'note_stack'} visible={!!post.memo} type={0} />
                <ListSymbol icon={'note_stack'} visible={!!post.playerMemo} type={2} />
                <ListSymbol icon={'shopping_cart'} visible={onSiteLines(post).length > 0} type={0} />
              </ListSymbols>
              {post.group > 0 && <ListColorExtension group={post.group} />}
            </ListItem>
          ))}
        </div>
      )}
      <ContextMenu type={'unregisteredplayer'}>
        <ContextMenuItem label={'E-Mail'} icon={'mail'} type={3} onClick={handleEmail} />
        <ContextMenuSeparator />
        <ContextMenuItem label={'Info'} icon={'question_mark'} type={0} onClick={handleInfo} />
        <ContextMenuSeparator />
        <ContextMenuItem label={'Wiederherstellen'} icon={'history'} type={2} onClick={handleRestore} />
        <ContextMenuSeparator />
        <ContextMenuItem label={'Dauerhaft löschen'} icon={'delete_forever'} type={1} onClick={handleHardDeleteRequest} />
      </ContextMenu>
      <Popup
        name='confirmharddeleteeventplayer'
        headerText='Dauerhaft löschen'
        confirmText='Dauerhaft löschen'
        type="warning"
        onConfirm={handleHardDeleteConfirm}
        withCancel
      >
        <p>Du bist dabei, diesen Spieler dauerhaft vom Event zu löschen. Dadurch gehen auch mit dem Spieler zusammenhängende Daten wie z.B. gesendete Emails verloren.</p>
      </Popup>
      <Popup name='unregisteredplayerinfo' confirmText='Ok' headerText='Info' type="info">
        {infoPost && (
          <div className="infoPopupBody">
            <p className="infoPopupName">{infoPost.firstName ?? 'N/A'} {infoPost.lastName ?? ''}</p>
            {groupLabelOf(infoPost) && (
              <div className="infoPopupSection">
                <span className="infoPopupLabel">Gruppe am Spieltag</span>
                <span className="infoPopupValue infoPopupGroup">
                  <ParticipantGroupDot row={infoPost} size='md' withTooltip={false} />
                  {groupLabelOf(infoPost)}
                </span>
              </div>
            )}
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Abgemeldet am</span>
              <span className="infoPopupValue">{formatDateTime(infoPost.deletedAt)}</span>
            </div>
            {infoPost.playerMemo && (
              <div className="infoPopupSection">
                <span className="infoPopupLabel">Spielermemo</span>
                <div className="memoOutput">{infoPost.playerMemo}</div>
              </div>
            )}
            {(infoPost.memo ?? '') && (
              <div className="infoPopupSection">
                <span className="infoPopupLabel">Memo</span>
                <div className="memoOutput">
                  {(infoPost.memo ?? '').split('\\n').map((zeile, index) => (
                    index % 2 === 0 ? <h4 key={index}>{zeile}</h4> : <p key={index}>{zeile}</p>
                  ))}
                </div>
              </div>
            )}
            <div className="infoPopupSection">
              <span className="infoPopupLabel">Preis</span>
              <span className="infoPopupValue">
                {infoPost.price == null && onSiteLines(infoPost).length === 0
                  ? 'Standard'
                  : formatEuro(getPlayerTotal(infoPost))}
                {infoPost.priceOverridden && <span className="infoPopupValueMeta"> · manuell gesetzt</span>}
              </span>
            </div>
            {[
              { label: 'Zusätze', lines: signupLines(infoPost) },
              { label: 'Artikel', lines: onSiteLines(infoPost) },
            ].filter(s => s.lines.length > 0).map(s => (
              <div key={s.label} className="infoPopupSection">
                <span className="infoPopupLabel">{s.label}</span>
                <div className="infoPopupArticleList">
                  {s.lines.map(l => (
                    <span key={l.id} className="infoPopupArticleRow">
                      <span>{formatAddOnQty(l.name, l.quantity)}</span>
                      <span>{formatEuro((Number(l.unitPrice) || 0) * (Number(l.quantity) || 0))}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Popup>
      <Popup
        name='confirmrestoretowaitlist'
        headerText='Spieler auf Warteliste setzen?'
        confirmText='Zur Warteliste hinzufügen'
        type="warning"
        onConfirm={handleConfirmRestoreToWaitlist}
        withCancel
      >
        {restoreWaitlistReason === 'addOn' ? (
          <p>
            {describeExhaustedAddOns(restoreWaitlistAddOns)}.
            <br />Der Spieler wird auf die <b>Zusatz-Warteliste</b> gesetzt und bekommt die Mail „{mailSlotLabel('equipment_waitlist')}“, sofern sie für seine Gruppe eingeschaltet ist.
          </p>
        ) : (
          <p>
            Der Spieltag ist ausgebucht.
            <br />Der Spieler wird auf die <b>Warteliste</b> gesetzt und bekommt die Mail „{mailSlotLabel('waitlist')}“, sofern sie für seine Gruppe eingeschaltet ist.
          </p>
        )}
        <button type="button" className="registrationsForceConfirmBtn" onClick={handleSkipWaitlistOnRestore}>
          <Icon className="registrationsForceConfirmIcon" icon="bolt" />
          {restoreWaitlistReason === 'addOn' ? 'Trotzdem hinzufügen (Zusatz-Limit überschreiten)' : 'Trotzdem hinzufügen (Warteliste überspringen)'}
        </button>
      </Popup>
    </main>
  );
}

export default UnregisteredPlayersSection;
